import { useCallback, useMemo, useReducer, useRef } from 'react'
import { fetchCommunity, fetchNearby, fetchSearchGlobal } from '../api/peopleDiscoveryApi'

export const LIST_STATUS = {
  IDLE: 'idle',
  LOADING: 'loading',
  READY: 'ready',
  ERROR: 'error'
}

const PAGE_LIMIT_DEFAULT = 20

const createListState = () => ({
  ids: [],
  byId: {},
  page: 1,
  hasMore: false,
  status: LIST_STATUS.IDLE,
  loadedOnce: false,
  errorCode: null
})

const initialState = {
  community: createListState(),
  nearby: createListState(),
  search: createListState(),
  requiresLocationSetup: false
}

const mergeUsers = (currentSlice, users = [], replace = false) => {
  if (replace) {
    const ids = []
    const byId = {}

    users.forEach(user => {
      const id = String(user.id)
      ids.push(id)
      byId[id] = user
    })

    return { ids, byId }
  }

  const byId = { ...currentSlice.byId }
  const ids = [...currentSlice.ids]

  users.forEach(user => {
    const id = String(user.id)
    if (!byId[id]) ids.push(id)
    byId[id] = { ...(byId[id] || {}), ...user }
  })

  return { ids, byId }
}

const reducePatchFollow = (slice, userId, isFollowing) => {
  if (!slice.byId[userId]) return slice

  return {
    ...slice,
    byId: {
      ...slice.byId,
      [userId]: {
        ...slice.byId[userId],
        isFollowing
      }
    }
  }
}

const reducer = (state, action) => {
  switch (action.type) {
    case 'REQUEST_START': {
      const slice = state[action.slice]
      return {
        ...state,
        [action.slice]: {
          ...slice,
          status: LIST_STATUS.LOADING,
          errorCode: null
        }
      }
    }

    case 'REQUEST_SUCCESS': {
      const slice = state[action.slice]
      const { ids, byId } = mergeUsers(slice, action.users, action.replace)

      return {
        ...state,
        [action.slice]: {
          ...slice,
          ids,
          byId,
          page: action.page,
          hasMore: Boolean(action.hasMore),
          status: LIST_STATUS.READY,
          loadedOnce: true,
          errorCode: null
        },
        ...(action.slice === 'nearby' && action.requiresLocationSetup !== undefined
          ? { requiresLocationSetup: Boolean(action.requiresLocationSetup) }
          : {})
      }
    }

    case 'REQUEST_ERROR': {
      const slice = state[action.slice]
      return {
        ...state,
        [action.slice]: {
          ...slice,
          status: LIST_STATUS.ERROR,
          loadedOnce: true,
          errorCode: action.errorCode || null
        }
      }
    }

    case 'SET_REQUIRES_LOCATION_SETUP': {
      return {
        ...state,
        requiresLocationSetup: Boolean(action.value)
      }
    }

    case 'RESET_SEARCH': {
      return {
        ...state,
        search: createListState()
      }
    }

    case 'PATCH_USER_FOLLOW_STATE': {
      const userId = String(action.userId)
      return {
        ...state,
        community: reducePatchFollow(state.community, userId, action.isFollowing),
        nearby: reducePatchFollow(state.nearby, userId, action.isFollowing),
        search: reducePatchFollow(state.search, userId, action.isFollowing)
      }
    }

    default:
      return state
  }
}

const isAbortError = (error) => {
  return error?.name === 'AbortError' || error?.code === 'ERR_CANCELED'
}

const usePeopleDiscovery = ({ pageLimit = PAGE_LIMIT_DEFAULT } = {}) => {
  const [state, dispatch] = useReducer(reducer, initialState)

  const controllersRef = useRef({
    community: null,
    nearby: null,
    search: null
  })

  const requestIdRef = useRef({
    community: 0,
    nearby: 0,
    search: 0
  })

  const beginRequest = useCallback((slice) => {
    if (controllersRef.current[slice]) {
      controllersRef.current[slice].abort()
    }

    const controller = new AbortController()
    controllersRef.current[slice] = controller
    requestIdRef.current[slice] += 1

    const requestId = requestIdRef.current[slice]
    dispatch({ type: 'REQUEST_START', slice })

    return { requestId, controller }
  }, [])

  const isStale = useCallback((slice, requestId) => {
    return requestId !== requestIdRef.current[slice]
  }, [])

  const loadCommunity = useCallback(async ({ page = 1, replace = false } = {}) => {
    const slice = 'community'
    const { requestId, controller } = beginRequest(slice)

    try {
      const data = await fetchCommunity({
        page,
        limit: pageLimit,
        signal: controller.signal
      })

      if (isStale(slice, requestId)) return

      dispatch({
        type: 'REQUEST_SUCCESS',
        slice,
        users: data.users || [],
        page,
        hasMore: data.hasMore,
        replace
      })
    } catch (error) {
      if (isAbortError(error) || isStale(slice, requestId)) return

      dispatch({
        type: 'REQUEST_ERROR',
        slice,
        errorCode: error?.response?.data?.code || null
      })
    }
  }, [beginRequest, isStale, pageLimit])

  const loadNearby = useCallback(async ({ page = 1, replace = false, coords = null } = {}) => {
    const slice = 'nearby'
    const { requestId, controller } = beginRequest(slice)

    try {
      const data = await fetchNearby({
        page,
        limit: pageLimit,
        lat: coords?.lat,
        lng: coords?.lng,
        signal: controller.signal
      })

      if (isStale(slice, requestId)) return

      dispatch({
        type: 'REQUEST_SUCCESS',
        slice,
        users: data.users || [],
        page,
        hasMore: data.hasMore,
        replace,
        requiresLocationSetup: data.requiresLocationSetup
      })
    } catch (error) {
      if (isAbortError(error) || isStale(slice, requestId)) return

      dispatch({
        type: 'REQUEST_ERROR',
        slice,
        errorCode: error?.response?.data?.code || null
      })
    }
  }, [beginRequest, isStale, pageLimit])

  const loadSearchGlobal = useCallback(async ({ q, page = 1, replace = false } = {}) => {
    const slice = 'search'
    const { requestId, controller } = beginRequest(slice)

    try {
      const data = await fetchSearchGlobal({
        q,
        page,
        limit: pageLimit,
        signal: controller.signal
      })

      if (isStale(slice, requestId)) return

      dispatch({
        type: 'REQUEST_SUCCESS',
        slice,
        users: data.users || [],
        page,
        hasMore: data.hasMore,
        replace
      })
    } catch (error) {
      if (isAbortError(error) || isStale(slice, requestId)) return

      if (error?.response?.data?.code === 'QUERY_TOO_SHORT') {
        dispatch({ type: 'RESET_SEARCH' })
        return
      }

      dispatch({
        type: 'REQUEST_ERROR',
        slice,
        errorCode: error?.response?.data?.code || null
      })
    }
  }, [beginRequest, isStale, pageLimit])

  const resetSearch = useCallback(() => {
    if (controllersRef.current.search) {
      controllersRef.current.search.abort()
      controllersRef.current.search = null
    }

    requestIdRef.current.search += 1
    dispatch({ type: 'RESET_SEARCH' })
  }, [])

  const patchUserFollowState = useCallback((userId, isFollowing) => {
    dispatch({
      type: 'PATCH_USER_FOLLOW_STATE',
      userId,
      isFollowing
    })
  }, [])

  const communityUsers = useMemo(() => state.community.ids.map(id => state.community.byId[id]).filter(Boolean), [state.community])
  const nearbyUsers = useMemo(() => state.nearby.ids.map(id => state.nearby.byId[id]).filter(Boolean), [state.nearby])
  const searchUsers = useMemo(() => state.search.ids.map(id => state.search.byId[id]).filter(Boolean), [state.search])

  return {
    community: state.community,
    nearby: state.nearby,
    search: state.search,
    requiresLocationSetup: state.requiresLocationSetup,
    communityUsers,
    nearbyUsers,
    searchUsers,
    loadCommunity,
    loadNearby,
    loadSearchGlobal,
    resetSearch,
    patchUserFollowState
  }
}

export default usePeopleDiscovery
